import React, { useState, useEffect } from 'react';
import { User, Mail, Save, UserCircle, Check, Loader2 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { useGetCurrentUserQuery, useUpdateProfileMutation } from '../../../store/services/api';
import { toast } from 'react-toastify';

const ProfileSettings = () => {
  const { data: user, isLoading: isLoadingUser, error: userError } = useGetCurrentUserQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.full_name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName.trim()) {
      setSaveError('Full name cannot be empty');
      return;
    }
    
    try {
      setSaveError(null);
      setSaveSuccess(false);
      
      await updateProfile({
        full_name: formData.fullName.trim()
      }).unwrap();
      
      // Show success message
      setSaveSuccess(true);
      toast.success('Profile updated successfully');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      const errorMessage = error?.data?.message || 'Failed to update profile';
      setSaveError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (saveError) {
      setSaveError(null);
    }
  };

  if (isLoadingUser) {
    return (
      <div className="space-y-6">
        <div className="bg-white p-5 rounded-lg border border-gray-100">
          <h1 className="text-xl font-semibold mb-1">Profile Settings</h1>
          <p className="text-sm text-gray-500">Loading your profile information...</p>
        </div>
        <div className="bg-white p-8 rounded-lg border border-gray-100 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#FF4444] animate-spin" />
        </div>
      </div>
    );
  }

  if (userError) {
    return (
      <div className="space-y-6">
        <div className="bg-white p-5 rounded-lg border border-gray-100">
          <h1 className="text-xl font-semibold mb-1">Profile Settings</h1>
          <p className="text-sm text-red-500">Failed to load profile information. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-5 rounded-lg border border-gray-100">
        <h1 className="text-xl font-semibold mb-1">Profile Settings</h1>
        <p className="text-sm text-gray-500">Manage your personal information and account details</p>
      </div>
      
      <div className="bg-white p-5 rounded-lg border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <UserCircle className="w-4 h-4 text-[#FF4444]" />
          <h2 className="text-base font-medium">Personal Information</h2>
        </div>

        {saveError && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
            {saveError}
          </div>
        )}
        
        {saveSuccess && (
          <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md text-sm flex items-center">
            <Check className="w-4 h-4 mr-2" />
            Profile updated successfully
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-4">
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Full Name
                </label>
                <div className="relative">
                  <input
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full pl-9 py-2.5 rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all text-sm"
                    placeholder="Enter your full name"
                  />
                  <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full pl-9 py-2.5 rounded-md border border-gray-200 bg-gray-100 cursor-not-allowed text-gray-600 focus:outline-none text-sm"
                  />
                  <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                <p className="text-xs text-gray-500 mt-1">Email address cannot be changed</p>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            className="bg-[#FF4444] text-white hover:bg-[#FF3333] h-9 text-sm"
            disabled={isSaving || isLoadingUser}
          >
            {isSaving ? (
              <>
                <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5 mr-1.5" />
                Save Changes
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProfileSettings;